/**
 * A horizontally scrolling area with a scrollbar at the TOP as well as the bottom.
 *
 * The orders table is thirteen columns wide and six hundred pixels tall. With only the
 * native scrollbar, reading the Age or Visibility column means scrolling to the bottom of
 * the table, dragging right, then scrolling back up — for every screenful. The column you
 * cannot reach is the one people are judged on, so this is not a nicety.
 *
 * The top bar is a mirror: an empty strip whose inner width is the real content width, so
 * the browser gives it a scrollbar of exactly the right proportions. The two panes track
 * each other's `scrollLeft`.
 *
 * ⚠ It renders only when the content actually overflows. An 8px strip above a table that
 * fits is a smudge people try to click.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

export function DualScrollArea({
  children,
  maxHeight,
  className = "",
}: {
  children: ReactNode;
  /** CSS height cap for the scrolling body, e.g. "620px". */
  maxHeight?: string;
  className?: string;
}) {
  const topRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [overflows, setOverflows] = useState(false);

  // Re-measure whenever the body or its content changes size: a filter that narrows the
  // table, a window resize, or fonts finishing loading all change whether it overflows.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    const measure = () => {
      setContentWidth(body.scrollWidth);
      setOverflows(body.scrollWidth > body.clientWidth + 1);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(body);
    if (body.firstElementChild) observer.observe(body.firstElementChild);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [children]);

  /**
   * Keep the two panes on the same column.
   *
   * ⚠ No re-entry flag, deliberately, and this was arrived at the hard way. The obvious
   * guard — set a flag, clear it in `requestAnimationFrame` — LATCHES wherever rAF does
   * not run: a background tab, a headless render, a machine under load. The flag stays
   * true and horizontal scrolling silently stops working for the rest of the session.
   * Measured: the first drag synced, every one after it did nothing.
   *
   * None is needed. A scroll event fires only when the position actually CHANGES, so
   * `a → b → a` terminates on the second assignment, and the equality check below means
   * the second assignment is not even made.
   */
  const sync = useCallback((from: HTMLDivElement | null, to: HTMLDivElement | null) => {
    if (!from || !to) return;
    if (to.scrollLeft !== from.scrollLeft) to.scrollLeft = from.scrollLeft;
  }, []);

  return (
    <>
      {overflows && (
        <div
          ref={topRef}
          onScroll={() => sync(topRef.current, bodyRef.current)}
          className="custom-scrollbar overflow-x-auto overflow-y-hidden border-b border-slate-100"
          // aria-hidden: it duplicates the body's own scrollbar, so a screen reader
          // announcing a second scroll region here would be noise, not help.
          aria-hidden="true"
        >
          <div style={{ width: `${contentWidth}px`, height: "1px" }} />
        </div>
      )}
      <div
        ref={bodyRef}
        onScroll={() => sync(bodyRef.current, topRef.current)}
        className={`custom-scrollbar overflow-x-auto ${className}`}
        style={maxHeight ? { maxHeight } : undefined}
      >
        {children}
      </div>
    </>
  );
}
